<template>
  <div class="refund-policy pb-10">
    <v-radio-group
      v-model="selectedRefundPolicy"
      class="dates-options"
    >
      <p class="title">Refund ticket:</p>
      <v-radio
        value="no-refunds"
        label="No refunds"
        color="var(--color-blue-800)"
        class="payment-option"
      />
      <v-radio
        value="day-before"
        label="Until day before event"
        color="var(--color-blue-800)"
        class="payment-option"
      />
      <v-radio
        value="two-days-before"
        label="Until 2 days before event"
        color="var(--color-blue-800)"
        class="payment-option"
      />
      <v-radio
        value="custom"
        label="Schedule custom refund policy"
        color="var(--color-blue-800)"
        class="payment-option"
      />
    </v-radio-group>

    <!-- Date and Time inputs - shown only when not "no-refunds" -->
    <div
      v-if="selectedRefundPolicy !== 'no-refunds'"
      class="refund-datetime mt-6"
    >
      <p class="refund-datetime__title">Refund deadline *</p>
      <div class="refund-datetime__inputs">
        <BKInput
          :value="displayRefundDate"
          label="Refund date"
          type="date"
          class="refund-datetime__date"
          :readonly="isReadonly"
          @input="refundDate = String($event)"
        />
        <BKInput
          :value="displayRefundTime"
          label="Refund time"
          type="time"
          class="refund-datetime__time"
          :readonly="isReadonly"
          @input="refundTime = String($event)"
        />
      </div>
    </div>

    <EventEditActionButtons
      :is-loading="isPending"
      :disabled="false"
      :error="errorMessage || error?.message"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted } from 'vue'

import BKInput from '@/components/AppShared/BKInput.vue'
import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import { ISetPaymentSystemPayloadEnabled } from '@/modules/Payment/types'
import { useSetPaymentSystem } from '@/modules/Payment/api/queries'

const { mutate: setPaymentSystem, isPending, error } = useSetPaymentSystem()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const errorMessage = ref<string>()

// Refund policy reactive variables
const selectedRefundPolicy = ref<string>('no-refunds')
const refundDate = ref('')
const refundTime = ref('12:00')

function toLocalDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function toLocalTimeString(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${h}:${min}`
}

// Helper function to create date from event date
function createDateFromEvent(daysToSubtract: number): { date: string; time: string } {
  const eventDate = new Date(props.eventData.dateStart)
  eventDate.setDate(eventDate.getDate() - daysToSubtract)

  return {
    date: toLocalDateString(eventDate),
    time: toLocalTimeString(eventDate),
  }
}

// Computed properties for displaying date/time values
const displayRefundDate = computed(() => {
  if (selectedRefundPolicy.value === 'no-refunds') return ''
  if (selectedRefundPolicy.value === 'custom') return refundDate.value

  // For premade variants, show computed date
  if (selectedRefundPolicy.value === 'day-before') {
    const { date } = createDateFromEvent(1)
    return date
  }

  if (selectedRefundPolicy.value === 'two-days-before') {
    const { date } = createDateFromEvent(2)
    return date
  }

  return refundDate.value
})

const displayRefundTime = computed(() => {
  if (selectedRefundPolicy.value === 'no-refunds') return ''
  if (selectedRefundPolicy.value === 'custom') return refundTime.value

  // For premade variants, show computed time
  if (selectedRefundPolicy.value === 'day-before') {
    const { time } = createDateFromEvent(1)
    return time
  }

  if (selectedRefundPolicy.value === 'two-days-before') {
    const { time } = createDateFromEvent(2)
    return time
  }

  return refundTime.value
})

const isReadonly = computed(() => {
  return selectedRefundPolicy.value !== 'custom'
})

function determineRefundPolicyFromDate(refundableUntil: string): string {
  if (!refundableUntil || refundableUntil === '' || refundableUntil === '0001-01-01T00:00:00Z') {
    return 'no-refunds'
  }

  const refundableUntilDate = new Date(refundableUntil)
  const eventDate = new Date(props.eventData.dateStart)

  const dayBeforeDate = new Date(eventDate)
  dayBeforeDate.setDate(dayBeforeDate.getDate() - 1)

  const twoDaysBeforeDate = new Date(eventDate)
  twoDaysBeforeDate.setDate(twoDaysBeforeDate.getDate() - 2)

  const refundDateOnly = refundableUntilDate.toISOString().split('T')[0]
  const dayBeforeDateOnly = dayBeforeDate.toISOString().split('T')[0]
  const twoDaysBeforeDateOnly = twoDaysBeforeDate.toISOString().split('T')[0]

  if (refundDateOnly === dayBeforeDateOnly) {
    return 'day-before'
  }
  if (refundDateOnly === twoDaysBeforeDateOnly) {
    return 'two-days-before'
  }
  return 'custom'
}

onMounted(() => {
  const refundableUntil = props.eventData.paymentSystem?.refundableUntil
  if (refundableUntil) {
    const policy = determineRefundPolicyFromDate(refundableUntil)
    selectedRefundPolicy.value = policy

    if (policy === 'custom' && refundableUntil !== '' && refundableUntil !== '0001-01-01T00:00:00Z') {
      const refundDateTime = new Date(refundableUntil)
      refundDate.value = toLocalDateString(refundDateTime)
      refundTime.value = toLocalTimeString(refundDateTime)
    }
  }
})

watch(selectedRefundPolicy, (newValue) => {
  if (newValue === 'day-before') {
    const { date, time } = createDateFromEvent(1)
    refundDate.value = date
    refundTime.value = time
  } else if (newValue === 'two-days-before') {
    const { date, time } = createDateFromEvent(2)
    refundDate.value = date
    refundTime.value = time
  } else if (newValue === 'custom') {
    if (!refundDate.value) {
      const { date, time } = createDateFromEvent(0)
      refundDate.value = date
      refundTime.value = time
    }
  }
})

function saveEventData(onSuccess: () => void) {
  if (isPending.value || !props.eventData.paymentSystem) return
  errorMessage.value = undefined

  const payload: ISetPaymentSystemPayloadEnabled = {
    ...props.eventData.paymentSystem,
    isEnabled: true,
    isRefundable: selectedRefundPolicy.value !== 'no-refunds',
    refundableUntil:
      selectedRefundPolicy.value === 'no-refunds'
        ? '0001-01-01T00:00:00Z'
        : new Date(`${displayRefundDate.value}T${displayRefundTime.value}:00`).toISOString(),
  }

  setPaymentSystem(
    {
      eventId: props.eventData.id,
      payload,
    },
    {
      onSuccess,
      onError: (err) => {
        errorMessage.value = err.message || 'An error occurred while saving refund policy.'
      },
    }
  )
}

function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.refund-policy {
  max-width: 410px;
  margin: 140px auto 60px;

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .form-title {
    margin-bottom: 0;
    color: var(--color-dark-800);
    font-weight: 700;
    font-size: 20px;
    line-height: 26px;
  }

  .learn-more-btn {
    padding: 4px;
    height: auto;
    min-height: 0;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-transform: none;
  }

  .payment-options {
    margin-top: 0;
    padding-top: 0;

    .payment-option {
      margin-bottom: 12px;
      padding: 16px 0;
    }
  }
}

.refund-datetime {
  max-width: 410px;
  margin: 0 auto;

  &__title {
    margin-bottom: 16px;
    color: var(--color-dark-800);
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
  }

  &__inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  &__date,
  &__time {
    margin-bottom: 16px;
  }
}
</style>
